import axios from 'axios';
import useAuthStore from 'clientStore/useAuthStore';
import useBranchStore from 'clientStore/useBranchStore';
import { isTokenExpired } from 'utils/jwt';

const request = axios.create({
  // eslint-disable-next-line no-undef
  baseURL: process.env.REACT_APP_BASE_URL,
  withCredentials: true
});

const SKIP_COMPANY_BRANCH_URLS = [
  '/auth/token',
  '/token/refresh',
  '/api/telegram/verify/',
  '/api/users/me',
  '/api/companies/'
];

const SKIP_BRANCH_PARAM_URLS = ['/api/branches/', '/api/company-payments/'];

function shouldAttachCompanyBranch(config) {
  const url = config?.url || '';
  return !SKIP_COMPANY_BRANCH_URLS.some((path) => url.includes(path));
}

function shouldAttachBranchParam(config) {
  const url = config?.url || '';
  return !SKIP_BRANCH_PARAM_URLS.some((path) => url.includes(path));
}

function attachCompanyBranchContext(config) {
  if (!shouldAttachCompanyBranch(config)) {
    return;
  }

  const { selectedCompanyId, selectedBranchId } = useBranchStore.getState();
  const attachBranch = shouldAttachBranchParam(config);

  if (selectedCompanyId == null && (!attachBranch || selectedBranchId == null)) {
    return;
  }

  // Only query params — custom headers trigger CORS preflight and break API calls.
  config.params = {
    ...(config.params || {}),
    ...(selectedCompanyId != null ? { company: selectedCompanyId } : {}),
    ...(attachBranch && selectedBranchId != null ? { branch: selectedBranchId } : {})
  };
}

// Add a request interceptor
request.interceptors.request.use(
  async function (config) {
    // Do something before request is sent
    const { accessToken, setAccessToken, setRefreshToken } = useAuthStore.getState();

    // Check if token is expired before making request
    if (accessToken && isTokenExpired(accessToken)) {
      // Token is expired, try to refresh
      const { refreshToken: currentRefreshToken } = useAuthStore.getState();
      
      if (currentRefreshToken && !isTokenExpired(currentRefreshToken)) {
        try {
          const { data } = await axios({
            method: 'POST',
            // eslint-disable-next-line no-undef
            baseURL: process.env.REACT_APP_BASE_URL,
            url: '/token/refresh',
            headers: { refresh: currentRefreshToken }
          });
          setAccessToken(data.access);
          setRefreshToken(data.refresh);
          config.headers['Authorization'] = `Bearer ${data.access}`;
        } catch (e) {
          // Refresh failed, logout
          localStorage.removeItem('authStore');
          setAccessToken(null);
          setRefreshToken(null);
          window.location.href = '/';
          return Promise.reject(e);
        }
      } else {
        // Refresh token also expired, logout
        localStorage.removeItem('authStore');
        setAccessToken(null);
        setRefreshToken(null);
        window.location.href = '/';
        return Promise.reject(new Error('Token expired'));
      }
    } else {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    attachCompanyBranchContext(config);

    return config;
  },
  function (error) {
    // Do something with request error
    return Promise.reject(error);
  }
);

// Add a response interceptor
request.interceptors.response.use(
  async function (response) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data

    return response;
  },
  async function (error) {
    const { refreshToken, setAccessToken, setRefreshToken } = useAuthStore.getState();

    if (error.response?.status === 401 || error.response?.status === 403) {
      try {
        const { data } = await axios({
          method: 'POST',
          // eslint-disable-next-line no-undef
          baseURL: process.env.REACT_APP_BASE_URL,
          url: '/token/refresh',
          headers: { refresh: refreshToken }
        });
        setAccessToken(data.access);
        setRefreshToken(data.refresh);
      } catch (e) {
        localStorage.removeItem('authStore');
        window.location.pathname = '/';
      }
    }
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    return Promise.reject(error);
  }
);

export default request;
